import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import {
  getBrightInvertedColor,
  getColorWithAlpha,
  scrollbarColoredMixin
} from '@elfsight/widget-utils';
import { CardLayout } from '../../lib/card';
import { usePost } from '../Post';
import {
  CardActionsBlock,
  CardContainer,
  CardMediaBlock,
  CardPreviewLinkBlock,
  CardRepostPreviewBlock,
  CardTextBlock,
  CardUserBlock
} from '../parts';
import { usePostSettings } from '../utils';

interface PopupClassicProps {
  space: number;
}

export const PopupClassic = forwardRef<HTMLDivElement, PopupClassicProps>(
  ({ space, ...forwardingProps }, forwardingRef) => {
    const { showMedia, showTextLink } = usePost();
    const {
      backgroundColor,
      borderRadius,
      source,
      user,
      textColor,
      showActions,
      showText,
      popupMediaBackgroundColor,
      showFollowButton = false
    } = usePostSettings();

    const showContentColumn =
      user.show || source.show || showActions || showText || showFollowButton;

    const scrollbarColor = getColorWithAlpha(
      getBrightInvertedColor(backgroundColor),
      0.3
    );

    return (
      <CardContainer
        {...forwardingProps}
        ref={forwardingRef}
        _space={space}
        _backgroundColor={backgroundColor}
        _borderRadius={borderRadius}
      >
        <CardLayout.Columns>
          <MediaColumn
            _scrollbarColor={scrollbarColor}
            _canOverflow={!showMedia}
          >
            {showMedia ? (
              <CardMediaBlock
                popupMediaBackgroundColor={popupMediaBackgroundColor}
                mode="scale-down"
              />
            ) : (
              <MediaColumnWrapper>
                <CardTextBlock centered />

                <CardRepostPreviewBlock />

                {showTextLink && <CardPreviewLinkBlock />}
              </MediaColumnWrapper>
            )}
          </MediaColumn>

          {showContentColumn && (
            <ContentColumn
              _color={textColor}
              _showFollowButton={showFollowButton}
            >
              <CardUserBlock withSource layout="classic" />

              {showMedia && <StyledCardTextBlock />}
              <CardActionsBlock />
            </ContentColumn>
          )}
        </CardLayout.Columns>
      </CardContainer>
    );
  }
);

PopupClassic.displayName = 'PopupClassicCard';

const StyledCardTextBlock = styled(CardTextBlock)`
  overflow-y: auto;
  flex-direction: column;
`;

const MediaColumn = styled.div<{
  _scrollbarColor: string;
  _canOverflow: boolean;
}>`
  flex: 3 !important;
  min-width: 390px;

  ${({ _scrollbarColor }) => scrollbarColoredMixin(_scrollbarColor)}

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ _scrollbarColor }) =>
      getColorWithAlpha(_scrollbarColor, 0.5)};
  }

  ${({ _canOverflow }) =>
    _canOverflow &&
    css`
      overflow: hidden auto;
    `}
`;

const MediaColumnWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ContentColumn = styled.div<{
  _color: string;
  _showFollowButton: boolean;
}>`
  min-width: ${({ _showFollowButton }) => (_showFollowButton ? 320 : 300)}px;
  border-left: ${({ _color }) => {
    const borderColor = _color.replace(/[^0-9,.]/g, '').split(',');
    borderColor[3] = '0.1';

    return `1px solid rgba(${borderColor.join(',')})`;
  }};
`;
